import React, { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { nanoid } from 'nanoid';

import { closeDialogAction, openDialogAction } from 'store/global/actions';

import CicFieldReasonsForm from '../../components/CicFieldReasonsForm';
import {
  CIC_FIELD_REASONS_MODAL_HEADER,
  NO_ITEMS,
} from '../../components/CicFieldReasonsForm/CicFieldReasonsForm.constants';

/**
 * Opens the CIC reason pop-up for a save the server answered with `cicReasonChanges` or `cicFeeChanges`.
 * Resolves with the `cicReasons` and `cicFeeReasons` request entries on confirm, or `null` when the user
 * abandons the save.
 */
export const useOpenCicFieldReasonsModal = () => {
  const dispatch = useDispatch();

  const resolveRef = useRef(null);
  const modalIdRef = useRef(null);

  const settle = useCallback(value => {
    resolveRef.current?.(value);
    resolveRef.current = null;
  }, []);

  const closeWith = useCallback(
    value => {
      settle(value);
      dispatch(closeDialogAction(modalIdRef.current));
    },
    [dispatch, settle],
  );

  const onConfirm = useCallback(reasons => closeWith(reasons), [closeWith]);

  const onCancel = useCallback(() => closeWith(null), [closeWith]);

  const onDismiss = useCallback(() => settle(null), [settle]);

  const renderBody = useCallback(
    ({
      changes,
      recalculatedFields,
      saveMessage,
      previousCicReasons,
      feeChanges,
      previousCicFeeReasons,
    }) =>
      () => (
        <CicFieldReasonsForm
          changes={changes}
          feeChanges={feeChanges || NO_ITEMS}
          previousCicFeeReasons={previousCicFeeReasons || NO_ITEMS}
          previousCicReasons={previousCicReasons || NO_ITEMS}
          recalculatedFields={recalculatedFields || NO_ITEMS}
          saveMessage={saveMessage || ''}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      ),
    [onCancel, onConfirm],
  );

  return useCallback(
    params => {
      settle(null);
      modalIdRef.current = nanoid();

      dispatch(
        openDialogAction({
          id: modalIdRef.current,
          header: CIC_FIELD_REASONS_MODAL_HEADER,
          isRestyled: true,
          size: ModalSize.LARGE,
          withClosingConfirmation: true,
          afterCloseCallback: onDismiss,
          body: renderBody(params),
        }),
      );

      return new Promise(resolve => {
        resolveRef.current = resolve;
      });
    },
    [dispatch, settle, onDismiss, renderBody],
  );
};
